<template>
  <UIDropdown ref="dropdownRef" class="UIGradientPickerDropdown" :items="[]">
    <!-- The trigger is the caller's sphere, not a select field. -->
    <template #toggle-container>
      <slot name="toggle" />
    </template>

    <!-- The whole panel is a form, not a list of rows. -->
    <template #content-container>
      <div class="UIGradientPickerDropdown__content">
        <div class="UIGradientPickerDropdown__header">
          <h1 class="UIGradientPickerDropdown__title">Gradient picker</h1>
          <UIButton
            class="UIGradientPickerDropdown__close-button"
            leadIcon="close"
            aria-label="Close"
            theme="secondary"
            size="sm"
            @click="handleCancel()"
          />
        </div>

        <div class="UIGradientPickerDropdown__body">
          <div
            class="UIGradientPickerDropdown__preview"
            :style="{ background: gradient }"
          />

          <div class="UIGradientPickerDropdown__configuration">
            <div class="UIGradientPickerDropdown__configuration-colors">
              <UIInputLabel>Colors</UIInputLabel>
              <div class="UIGradientPickerDropdown__color-input-wrapper">
                <div
                  class="UIGradientPickerDropdown__color-preview"
                  :style="{ backgroundColor: color1 }"
                  @click="openColor1Picker"
                >
                  <input
                    ref="color1Input"
                    type="color"
                    :value="color1"
                    class="UIGradientPickerDropdown__color-input"
                    @input="updateColor1"
                  />
                </div>
                <div
                  class="UIGradientPickerDropdown__color-preview"
                  :style="{ backgroundColor: color2 }"
                  @click="openColor2Picker"
                >
                  <input
                    ref="color2Input"
                    type="color"
                    :value="color2"
                    class="UIGradientPickerDropdown__color-input"
                    @input="updateColor2"
                  />
                </div>
              </div>
            </div>

            <div class="UIGradientPickerDropdown__configuration-angle">
              <UIInputLabel>Direction angle</UIInputLabel>
              <UIInputTextBase
                ref="angleField"
                class="UIGradientPickerDropdown__angle-input"
                name="angle"
                type="number"
                :modelValue="angle"
                placeholder="180°"
                @update:modelValue="updateAngle"
              />
            </div>
          </div>

          <div class="UIGradientPickerDropdown__actions">
            <UIButton theme="secondary" size="md" @click="handleCancel()">
              Cancel
            </UIButton>
            <UIButton theme="primary" size="md" @click="handleDone()">
              Done
            </UIButton>
          </div>
        </div>
      </div>
    </template>
  </UIDropdown>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from 'vue'

import {
  UIButton,
  UIDropdown,
  UIInputLabel,
  UIInputTextBase,
} from '@foundever/ui-lib'

import { redactCardNumbers } from '@/utils/cardNumberRedaction'

const props = defineProps<{
  modelValue?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  selected: []
}>()

// Parse the initial gradient to extract colors and angle
const parseGradient = (gradient: string) => {
  const match = gradient.match(/linear-gradient\(([^)]+)\)/)
  if (!match) return { color1: '#ccccff', color2: '#4b4bf9', angle: 207 }

  const content = match[1]
  const angleMatch = content.match(/(\d+(?:\.\d+)?)deg/)
  const angle = angleMatch ? Math.round(parseFloat(angleMatch[1])) : 207

  // Try to find hex colors first
  let colorMatches = content.match(/#[A-Fa-f0-9]{6}/g)

  // If no hex colors found, try to find rgb colors and convert them
  if (!colorMatches || colorMatches.length < 2) {
    const rgbMatches = content.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g)
    if (rgbMatches && rgbMatches.length >= 2) {
      colorMatches = rgbMatches.map((rgb) => {
        const rgbMatch = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
        if (rgbMatch && rgbMatch.length >= 4) {
          const r = parseInt(rgbMatch[1] ?? '0')
          const g = parseInt(rgbMatch[2] ?? '0')
          const b = parseInt(rgbMatch[3] ?? '0')
          return `#${r.toString(16).padStart(2, '0')}${g
            .toString(16)
            .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        }
        return '#ccccff'
      })
    }
  }

  const color1 = colorMatches?.[0] || '#ccccff'
  const color2 = colorMatches?.[1] || '#4b4bf9'

  return { color1, color2, angle }
}

const initialValues = parseGradient(
  props.modelValue ||
    'linear-gradient(207deg, rgb(204, 204, 255) 16.67%, rgb(75, 75, 249) 100%)',
)

const color1 = ref(initialValues.color1)
const color2 = ref(initialValues.color2)
const angle = ref<number>(initialValues.angle)

// Store original values for cancel functionality
const originalColor1 = ref(initialValues.color1)
const originalColor2 = ref(initialValues.color2)
const originalAngle = ref<number>(initialValues.angle)

const color1Input = ref<HTMLInputElement>()
const color2Input = ref<HTMLInputElement>()

const gradient = computed(() => {
  return `linear-gradient(${Math.round(angle.value)}deg, ${
    color1.value
  } 16.67%, ${color2.value} 100%)`
})

const openColor1Picker = () => {
  color1Input.value?.click()
}

const openColor2Picker = () => {
  color2Input.value?.click()
}

const updateColor1 = (event: Event) => {
  const target = event.target as HTMLInputElement
  color1.value = target.value
}

const updateColor2 = (event: Event) => {
  const target = event.target as HTMLInputElement
  color2.value = target.value
}

const angleField = ref<InstanceType<typeof UIInputTextBase>>()
const updateAngle = (value: string | number) => {
  // The angle is written into the saved gradient string, so a card number typed
  // here is dropped before it can get there.
  const angleText = redactCardNumbers(String(value), {
    inputElement: angleField.value?.inputElement,
    shouldRemove: true,
  })
  angle.value = parseInt(angleText) || 180
}

const dropdownRef =
  useTemplateRef<InstanceType<typeof UIDropdown>>('dropdownRef')
// ui-lib's UIDropdown has no close(); its anchor toggles on click. Only called from
// inside the open panel.
const hide = () => dropdownRef.value?.$refs.anchorElement?.click()

const handleDone = () => {
  emit('update:modelValue', gradient.value)
  emit('selected')
  hide()
}

const handleCancel = () => {
  // Revert to original values
  color1.value = originalColor1.value
  color2.value = originalColor2.value
  angle.value = originalAngle.value
  hide()
}

// Watch for prop changes to update local state
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      const parsed = parseGradient(newValue)
      color1.value = parsed.color1
      color2.value = parsed.color2
      angle.value = parsed.angle
      // Update original values when prop changes
      originalColor1.value = parsed.color1
      originalColor2.value = parsed.color2
      originalAngle.value = parsed.angle
    }
  },
  { immediate: true },
)
</script>

<style lang="scss">
// The picker is a form, so it is inset like one rather than like a list (ui-lib pads
// the panel for rows and keeps the right edge tight for a scrollbar).
.UIDropdown__content:has(.UIGradientPickerDropdown__content) {
  padding: var(---fds-global-space-sm);
}
</style>

<style lang="scss" scoped>
.UIGradientPickerDropdown__content {
  // background: cyan;
  width: 18.75rem;

  .UIGradientPickerDropdown__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .UIGradientPickerDropdown__title {
    @include font-size-16px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .UIGradientPickerDropdown__close-button {
    --color: var(--icon-primary);
    --size: 0.875rem;
  }

  .UIGradientPickerDropdown__body {
    margin-top: 0.75rem;
    display: grid;
    gap: 0.75rem;
  }

  .UIGradientPickerDropdown__preview {
    background: var(--bg-secondary);
    width: 18.75rem;
    height: 6.25rem;
  }

  .UIGradientPickerDropdown__configuration {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 0.5rem;
  }

  .UIGradientPickerDropdown__configuration-colors {
    display: grid;
  }

  .UIGradientPickerDropdown__color-input-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    position: relative;
  }

  .UIGradientPickerDropdown__color-preview {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 4px;
    border: 1px solid var(--interactive-border-default);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      border-color: var(--interactive-border-hover);
      transform: scale(1.05);
    }
  }

  .UIGradientPickerDropdown__color-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
    bottom: -0.25rem;
    right: 0;
  }

  .UIGradientPickerDropdown__angle-input {
    :deep(.UIInputTextBase__field) {
      height: 2.25rem;
    }
  }

  .UIGradientPickerDropdown__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
  }
}
</style>
